import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import {
  Button,
  Checkbox,
  FieldGroup,
  Header,
  Input,
  Label,
  Text,
  Loader,
  Fields,
} from '@pet-notify/ui'

import useDocumentTitle from 'hooks/document-title'
import ErrorMessage from 'components/ErrorMessage'
import { isEmailAvailable } from 'services/users'
import type { OrderRedemptionInput } from 'services/order-redemtions'

const schema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup
    .string()
    .email('Email must be a valid email address')
    .required('Email is required')
    .test('is-unique', 'Email is already in use', async (email) => {
      if (email) {
        try {
          const response = await isEmailAvailable(email)
          return response
        } catch (error) {
          return false
        }
      }

      return false
    }),
  confirmEmail: yup
    .string()
    .required('Confirm Email is required')
    .oneOf([yup.ref('email'), null], 'Must match email'),
  agree: yup
    .boolean()
    .oneOf(
      [true],
      'Please verify you understand that Pet Notify is only available in the US and Canada',
    ),
})

export type FormData = {
  firstName: string
  lastName: string
  email: string
  confirmEmail: string
}

type Props = {
  onSubmit: (v: FormData) => void
  state: OrderRedemptionInput
}

function PremiumSignupUserInfoPage({ state, ...props }: Props) {
  useDocumentTitle('Create Account | Pet Notify')
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: state.user,
  })

  const onSubmit = handleSubmit(props.onSubmit)

  if (isSubmitting) {
    return <Loader />
  }

  return (
    <>
      <form onSubmit={onSubmit}>
        <Text variant='note' mb='2'>
          Step 1 of 4
        </Text>

        <Header mb='6'>Welcome to Pet Notify!</Header>

        <Text mb='4'>
          Create your account to customize your Intellitag and start
          protecting your pets.
        </Text>

        <Fields>
          <FieldGroup>
            <Label htmlFor='signup-first-name'>First Name</Label>
            <Input id='signup-first-name' {...register('firstName')} />
          </FieldGroup>

          {errors.firstName && (
            <ErrorMessage error={errors.firstName.message} />
          )}

          <FieldGroup>
            <Label htmlFor='signup-last-name'>Last Name</Label>
            <Input id='signup-last-name' {...register('lastName')} />
          </FieldGroup>

          {errors.lastName && <ErrorMessage error={errors.lastName.message} />}

          <FieldGroup>
            <Label htmlFor='signup-email'>Email</Label>
            <Input
              type='email'
              id='signup-email'
              {...register('email', { required: true })}
            />
          </FieldGroup>

          {errors.email && <ErrorMessage error={errors.email.message} />}

          <FieldGroup>
            <Label htmlFor='signup-confirm-email'>Confirm email</Label>
            <Input
              type='email'
              id='signup-confirm-email'
              {...register('confirmEmail', { required: true })}
            />
          </FieldGroup>

          {errors.confirmEmail && (
            <ErrorMessage error={errors.confirmEmail.message} />
          )}

          {/* <FieldGroup>
            <Checkbox
              label='I understand Pet Notify is only available in the US and Canada'
              id='signup-agree'
              {...register('agree')}
            />
          </FieldGroup>

          {errors.agree && <ErrorMessage error={errors.agree.message} />} */}
        </Fields>
        <Button type='submit'>Create account</Button>
      </form>
    </>
  )
}

export default PremiumSignupUserInfoPage
